// Webhooks pessoais do aluno. Versão compacta que vive dentro de "Minha Conta"
// (consistente com BiometriaSection).
// Aluno só recebe eventos onde ele aparece (pagamento dele, aula dele).

import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Loader2,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Trash2,
  Webhook,
  X,
  XCircle
} from "lucide-react";
import {
  api,
  type UserWebhookEventLog,
  type UserWebhookSubscription
} from "./api";
import { openAlert } from "./ConfirmPromptModal";

export function WebhooksSection() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [subs, setSubs] = useState<UserWebhookSubscription[]>([]);
  const [events, setEvents] = useState<UserWebhookEventLog[]>([]);
  const [allowedEvents, setAllowedEvents] = useState<string[]>([]);
  const [secretToShow, setSecretToShow] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  async function reload() {
    try {
      const r = await api.listWebhooks();
      setSubs(r.subscriptions);
      setEvents(r.events);
      setAllowedEvents(r.allowedEvents);
      setPhase("ready");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function toggleActive(s: UserWebhookSubscription) {
    try {
      await api.patchWebhook(s.id, { active: !s.active });
      void reload();
    } catch (e) {
      void openAlert({ title: "Erro", message: e instanceof Error ? e.message : String(e) });
    }
  }
  async function rotate(s: UserWebhookSubscription) {
    if (!confirm("Gerar novo secret? O antigo deixará de funcionar.")) return;
    try {
      const r = await api.rotateWebhookSecret(s.id);
      setSecretToShow(r.secret);
      void reload();
    } catch (e) {
      void openAlert({ title: "Erro", message: e instanceof Error ? e.message : String(e) });
    }
  }
  async function remove(s: UserWebhookSubscription) {
    if (!confirm(`Remover webhook ${s.url}?`)) return;
    try {
      await api.deleteWebhook(s.id);
      void reload();
    } catch (e) {
      void openAlert({ title: "Erro", message: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <section class="rounded-md border border-border bg-card/60 p-5">
      <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 class="flex items-center gap-2 text-sm font-bold">
          <Webhook className="size-4 text-primary" />
          Webhooks
        </h2>
        <button
          type="button"
          onClick={() => setCreating(true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="size-3.5" /> Adicionar
        </button>
      </div>
      <p class="mb-3 text-xs text-muted-foreground">
        Notifique seu sistema externo (ou um Zapier/n8n) quando algo acontece
        com você — pagamento confirmado, aula registrada. HMAC-SHA256 em{" "}
        <code>x-webhook-signature</code>.
      </p>

      {phase === "loading" && (
        <p class="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> Carregando…
        </p>
      )}
      {phase === "error" && (
        <p class="flex items-center gap-2 text-xs text-destructive">
          <AlertCircle className="size-3.5" /> {err}
        </p>
      )}
      {phase === "ready" && subs.length === 0 && (
        <p class="text-xs italic text-muted-foreground">
          Nenhum webhook cadastrado.
        </p>
      )}
      {phase === "ready" && subs.length > 0 && (
        <ul class="space-y-2">
          {subs.map((s) => (
            <li
              key={s.id}
              class="flex flex-wrap items-center gap-3 rounded-md border border-border bg-background/60 px-3 py-2"
            >
              <div
                class={`grid size-8 shrink-0 place-items-center rounded-md ${
                  s.active
                    ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <Webhook className="size-4" />
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="truncate font-mono text-xs font-semibold">
                    {s.url}
                  </span>
                  {!s.active && (
                    <span class="rounded bg-muted px-1.5 py-0.5 text-[9px] font-bold uppercase text-muted-foreground">
                      pausado
                    </span>
                  )}
                </div>
                <div class="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                  <span class="rounded bg-primary/10 px-1 py-0.5 font-mono text-primary">
                    {s.secretPreview}
                  </span>
                  {s.events.map((e) => (
                    <span
                      key={e}
                      class="rounded bg-blue-500/10 px-1 py-0.5 font-mono text-blue-700 dark:text-blue-400"
                    >
                      {e}
                    </span>
                  ))}
                </div>
              </div>
              <div class="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => toggleActive(s)}
                  title={s.active ? "Pausar" : "Reativar"}
                  aria-label={s.active ? "Pausar" : "Reativar"}
                  class="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  {s.active ? (
                    <Pause className="size-3.5" />
                  ) : (
                    <Play className="size-3.5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => rotate(s)}
                  title="Rotacionar secret"
                  aria-label="Rotacionar secret"
                  class="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <RefreshCw className="size-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(s)}
                  title="Excluir"
                  aria-label="Excluir"
                  class="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {events.length > 0 && (
        <div class="mt-4">
          <h3 class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Últimas entregas
          </h3>
          <ul class="space-y-1">
            {events.slice(0, 5).map((e) => (
              <li
                key={e.id}
                class="flex flex-wrap items-center gap-2 rounded border border-border bg-background/40 px-2 py-1 text-[11px]"
              >
                <EventStatusBadge status={e.status} />
                <span class="font-mono">{e.kind}</span>
                <span class="text-muted-foreground">
                  · {new Date(e.createdAt).toLocaleString("pt-BR")}
                </span>
                {e.lastStatusCode != null && (
                  <span class="ml-auto font-mono text-muted-foreground">
                    HTTP {e.lastStatusCode}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {creating && (
        <CreateWebhookModal
          allowed={allowedEvents}
          onClose={() => setCreating(false)}
          onCreated={(secret) => {
            setSecretToShow(secret);
            setCreating(false);
            void reload();
          }}
        />
      )}

      {secretToShow && (
        <SecretRevealModal
          secret={secretToShow}
          onClose={() => setSecretToShow(null)}
        />
      )}
    </section>
  );
}

function EventStatusBadge({
  status
}: {
  status: UserWebhookEventLog["status"];
}) {
  if (status === "delivered")
    return (
      <span class="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1 py-0.5 text-[9px] font-bold text-emerald-700 dark:text-emerald-400">
        <CheckCircle2 className="size-3" />
        OK
      </span>
    );
  if (status === "failed")
    return (
      <span class="inline-flex items-center gap-1 rounded bg-destructive/15 px-1 py-0.5 text-[9px] font-bold text-destructive">
        <XCircle className="size-3" />
        falhou
      </span>
    );
  return (
    <span class="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1 py-0.5 text-[9px] font-bold text-amber-700 dark:text-amber-400">
      <Loader2 className="size-3 animate-spin" />
      pendente
    </span>
  );
}

function CreateWebhookModal({
  allowed,
  onClose,
  onCreated
}: {
  allowed: string[];
  onClose: () => void;
  onCreated: (secret: string) => void;
}) {
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>(["*"]);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggleEvent(ev: string) {
    if (ev === "*") {
      setEvents(["*"]);
      return;
    }
    const without = events.filter((e) => e !== "*" && e !== ev);
    if (events.includes(ev))
      setEvents(without.length > 0 ? without : ["*"]);
    else setEvents([...without, ev]);
  }

  async function submit() {
    setErr(null);
    if (!url.trim()) {
      setErr("URL é obrigatória.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.createWebhook({
        url: url.trim(),
        events,
        description: description.trim()
      });
      onCreated(r.secret);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="w-full max-w-lg rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Webhook className="size-4 text-primary" />
            Novo webhook
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="space-y-4 px-5 py-4 text-sm">
          <div class="space-y-1.5">
            <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              URL de destino
            </label>
            <input
              type="url"
              value={url}
              onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
              placeholder="https://exemplo.com/webhook"
              title="URL de destino"
              aria-label="URL de destino"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
            />
          </div>
          <div class="space-y-1.5">
            <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Eventos
            </label>
            <div class="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => toggleEvent("*")}
                class={`rounded-md border px-2.5 py-1 text-[11px] font-mono transition-colors ${
                  events.includes("*")
                    ? "border-primary bg-primary/15 text-primary"
                    : "border-border bg-background text-muted-foreground hover:bg-accent"
                }`}
              >
                * (todos seus)
              </button>
              {allowed.map((ev) => (
                <button
                  key={ev}
                  type="button"
                  onClick={() => toggleEvent(ev)}
                  class={`rounded-md border px-2.5 py-1 text-[11px] font-mono transition-colors ${
                    events.includes(ev)
                      ? "border-primary bg-primary/15 text-primary"
                      : "border-border bg-background text-muted-foreground hover:bg-accent"
                  }`}
                >
                  {ev}
                </button>
              ))}
            </div>
          </div>
          <div class="space-y-1.5">
            <label class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Descrição (opcional)
            </label>
            <input
              type="text"
              value={description}
              onInput={(e) => setDescription((e.target as HTMLInputElement).value)}
              placeholder="ex.: sync com Notion"
              title="Descrição"
              aria-label="Descrição"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          {err && (
            <p class="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="size-3.5" />
              {err}
            </p>
          )}
        </div>
        <footer class="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
            Criar
          </button>
        </footer>
      </div>
    </div>
  );
}

function SecretRevealModal({
  secret,
  onClose
}: {
  secret: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="w-full max-w-lg rounded-md border border-amber-500/40 bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="border-b border-border px-5 py-4">
          <h2 class="flex items-center gap-2 text-base font-bold text-amber-700 dark:text-amber-400">
            <AlertCircle className="size-4" />
            Copie este secret agora
          </h2>
          <p class="mt-1 text-xs text-muted-foreground">
            Mostramos só agora. Use pra validar o HMAC em{" "}
            <code>x-webhook-signature</code>.
          </p>
        </header>
        <div class="px-5 py-4">
          <code class="block break-all rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-xs">
            {secret}
          </code>
        </div>
        <footer class="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-5 py-3">
          <button
            type="button"
            onClick={copy}
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
          >
            <Copy className="size-3.5" /> {copied ? "Copiado!" : "Copiar"}
          </button>
          <button
            type="button"
            onClick={onClose}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Entendi
          </button>
        </footer>
      </div>
    </div>
  );
}
